import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { apiClient } from "../../../../api/client";

/**
 * Bitácora de auditoría — `superadmin/audit/` (TASK-21).
 * Acceso: PLATFORM_ADMIN y READ_ONLY (los demás roles reciben 403).
 *
 * Restricciones del backend que condicionan la UI:
 *   - Paginación DRF `{count, next, previous, results}` con **`?page` como único
 *     parámetro**: `page_size` se ignora y el tamaño es fijo (20). Por eso la
 *     página NO ofrece un selector de filas por página — parecería funcionar.
 *   - Orden fijo (`-created_at, -id`), sin parámetro `ordering`.
 *   - Filtros: `actor` (id numérico), `action` e `resource` (iexact), `from` y
 *     `to` (ISO o fecha simple, interpretada en America/Bogota).
 */
const AUDIT_PAGE_SIZE = 20;

function useAuditLog({ page = 1, actor, action, resource, from, to } = {}) {
  const filters = {
    actor: actor || null,
    action: action || null,
    resource: resource || null,
    from: from || null,
    to: to || null,
  };

  return useQuery({
    // Los filtros y la página forman parte de la clave: la paginación es
    // server-side y cada combinación es una entrada de caché distinta.
    queryKey: ["superadmin", "audit", { page, ...filters }],
    // A diferencia del resto de hooks de superadmin, aquí NO se desenvuelve
    // `.results`: la UI necesita `count`/`next`/`previous` para paginar.
    queryFn: () =>
      apiClient
        .get("/api/v1/superadmin/audit/", {
          params: Object.fromEntries(
            Object.entries({ page, ...filters }).filter(
              ([, v]) => v != null && v !== ""
            )
          ),
        })
        .then((r) => r.data),
    // Evita el parpadeo de tabla vacía al cambiar de página.
    placeholderData: keepPreviousData,
  });
}

export { useAuditLog, AUDIT_PAGE_SIZE };
